import { Button, Tag, Tooltip, Select } from "antd";
import React, { useState } from "react";
import dayjs from "dayjs";
import { NotesIcon, InfoIcon, UploadIcon, DeleteIcon } from "../../components/ActionIcons";
import { OrderModalityCell } from "../../components/ReconcileCells";
import { FieldLabels, Permissions, getOrdersStatusLabel } from "../../constants/fieldLabels";
import { hasPermission, formatAgeShort as formatAgeShortHelper, STUDY_DATETIME_FORMAT } from "../../utils/helper";
import { summarizeShortcut, isShortcutActive } from "../../utils/shortcuts";
import { isTechnicianAssignable } from "../../utils/modalityAssignment";
// Shared across every table's Status column — never fork a local copy.
import { getStatusBadgeStyle as getStatusStyle } from "../../utils/statusBadgeStyle";

// Shared 2-line clamp style for text cells (wrap once, then ellipsis), paired
// with a Tooltip for the full value. Matches the PACS/Worklist cell renderers'
// TEXT_CLAMP_2 (components/PacsAgGrid/cellRenderers.tsx) rather than the old
// single-line nowrap+ellipsis truncation.
const ellipsisCell: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  width: '100%',
  lineHeight: 1.2,
};

// Compact DICOM-style age ("39Y") to match the reference order list.
const formatAgeShort = (rec): string =>
  formatAgeShortHelper(rec?.patient?.pat_dob, rec?.patient?.pat_age) || '';

// HL7 patient class (stored O / I / E) -> human Reg. Type shown in the reference.
const formatRegType = (v): string => {
  const s = String(v ?? '').trim().toUpperCase();
  if (!s) return '';
  if (s.startsWith('O')) return 'OPD';
  if (s.startsWith('I')) return 'IPD';
  if (s.startsWith('E')) return 'ER';
  return s;
};

const titleCase = (s): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

const formatDateTime = (d): string | null =>
  d ? dayjs(d).format(STUDY_DATETIME_FORMAT) : null;



export const orderColumns = ({
  canDelete = false,
  onDelete,
  // Write-side enhancements — technician assignment + documentation uploads.
  // The Orders page is also shown read-only to radiologists / HODs, who get the
  // view actions below but none of these.
  isTechnician = false,
  // View actions (notes + patient/order info icons). Available to every role
  // that can open the Orders list, not just technicians.
  showInfoActions = isTechnician,
  // The "Check" (re-resolve modality) button writes to the order, so it is
  // limited to the roles that own the order (technicians).
  allowModalityRecheck = isTechnician,
  addFile,
  viewNotes,
  showOrdDetails,
  assignTechnician,
  userList = [],
  currentUser,
  dominantModalities,
  statusColumnTitle = String(FieldLabels.STATUS),
} = {}) => {
  // Technician assignment options are built per-order from the technician's
  // ASSIGNMENT modality (user_modalities.um_modality — a single code, or null
  // for a floater who can take anything). This is deliberately NOT um_list:
  // um_list only controls which orders a user is shown.
  //
  // Hybrid exams matter here — po_modality is a csv ("CT,PT" for PET-CT) and
  // such a study is owned by its dominant modality, so a CT technician is not
  // offered for a PET-CT; only a PT technician (or a floater) is. The rule
  // lives in utils/modalityAssignment, mirroring the backend.
  //
  // The current user stays pinned to the top as "(Self Assign)" when they
  // qualify, and the already-assigned technician is always kept in the list so
  // the dropdown keeps showing their name even if they no longer qualify.
  const currentUsername = currentUser?.username;
  const technicians = (userList || []).filter(u => u?.user_type === 'technician');
  const technicianOptionsForOrder = rec => {
    const assigned = rec?.order_workflow?.ow_assigned_technician;
    const qualifies = u =>
      u?.username === assigned ||
      isTechnicianAssignable(
        u?.user_modalities?.um_modality,
        rec?.po_modality,
        dominantModalities
      );
    const eligible = technicians.filter(qualifies);

    return eligible.map(u => {
      const isSelf = u.username === currentUsername;
      const baseLabel = u.user_fullname || u.username;
      const label = isSelf
        ? `${baseLabel || currentUser?.user_fullname || currentUsername} (Self Assign)`
        : baseLabel;
      return { label: String(label).trim(), value: u.username };
    }).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  };

  // Nameless column rendered just before Patient ID with a "show notes" icon.
  const notesIconColumn = {
    key: 'notes_icon',
    title: '',
    width: 25,
    minWidth: 25,
    align: 'center',
    render: (_text, rec) => {
      const hasNotes = rec?.note_id;
      return (
        <Tooltip title="View Notes">
          <Button
            type="text"
            size="small"
            icon={<NotesIcon size={18} disabled={false} variant={hasNotes ? 'blue' : 'grey'} />}
            onClick={e => {
              e.stopPropagation();
              if (viewNotes) viewNotes(rec);
            }}
          />
        </Tooltip>
      );
    },
  };

  // Nameless column rendered right after the notes icon with a "patient & order
  // info" icon.
  const infoIconColumn = {
    key: 'info_icon',
    title: '',
    width: 25,
    minWidth: 25,
    align: 'center',
    render: (_text, rec) => (
      <Tooltip title="Patient & Order Info">
        <Button
          type="text"
          size="small"
          icon={<InfoIcon size={18} />}
          onClick={e => {
            e.stopPropagation();
            showOrdDetails && showOrdDetails(rec);
          }}
        />
      </Tooltip>
    ),
  };

  // Technician assignment dropdown, placed right after Acc No.
  const technicianColumn = {
    dataIndex: 'assigned_to_tech',
    key: 'assigned_to_tech',
    title: FieldLabels.TECHNICIAN,
    width: 160,
    render: (_text, rec, ind) => {
      const assigned = rec?.order_workflow?.ow_assigned_technician;
      return (
        <Select
          value={assigned || undefined}
          placeholder="Assign technician"
          size="small"
          className="tech-assign-select"
          style={{ width: '100%', minWidth: 150 }}
          showSearch
          // Clearing (the ×) fires onChange with undefined. assignTechnician
          // treats an empty value as an unassign (after confirming), mirroring
          // the Scan List. CSS keeps the × always visible, not hover-only.
          allowClear
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={technicianOptionsForOrder(rec)}
          onChange={value => assignTechnician && assignTechnician(rec, value, ind)}
          onClick={e => e.stopPropagation()}
        />
      );
    },
  };

  // Actions column for technicians: documentation uploads.
  const technicianActionsColumn = {
    dataIndex: 'actions',
    key: 'actions',
    title: FieldLabels.ACTIONS,
    fixed: 'right',
    width: 56,
    render: (_text, rec) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {hasPermission(Permissions.CREATE_TECH_NOTES) && (
          <Tooltip title="Documentation Uploads">
            <Button
              type="text"
              size="small"
              icon={<UploadIcon size={18} />}
              onClick={e => {
                e.stopPropagation();
                addFile && addFile(rec);
              }}
            />
          </Tooltip>
        )}
      </div>
    ),
  };

  // Column order mirrors the reference RIS order list so every column fits on a
  // normal-width screen without horizontal scroll. Patient Name and Item Desc.
  // flex to absorb spare width; everything else has a tight fixed width. Body
  // Part and the Assigned-To text column were dropped (Body Part is folded into
  // Item Desc., assignment is the technician dropdown). There is no payment field
  // in the schema, so the reference "Payment" column is intentionally omitted.
  return [
    notesIconColumn,
    infoIconColumn,
  {
    dataIndex: 'po_pin',
    key: 'po_pin',
    title: FieldLabels.PATIENT_ID,
    width: 90,
    render: (text, rec) => {
      const patPin = rec?.patient?.pat_pin;
      return (
        <Tooltip title={patPin}>
          <div style={ellipsisCell}>{patPin}</div>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_pat_name',
    key: 'po_pat_name',
    title: FieldLabels.PATIENT_NAME,
    flex: 2,
    minWidth: 150,
    render: (text, record) => {
      // Emergency cases are indicated by a reddish row tint (getRowClass),
      // not an inline alert icon next to the patient name.
      const patientName = record.patient?.pat_name;
      return (
        <Tooltip title={patientName}>
          <span className="col-pat-name" style={ellipsisCell}>{patientName}</span>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'patient.pat_age',
    key: 'pat_age',
    title: FieldLabels.PATIENT_REPORT_AGE,
    width: 56,
    align: 'center',
    render: (_text, rec) => formatAgeShort(rec),
  },
  {
    dataIndex: 'patient.pat_sex',
    key: 'pat_sex',
    title: FieldLabels.PATIENT_REPORT_SEX,
    width: 48,
    align: 'center',
    render: (_text, rec) => rec?.patient?.pat_sex,
  },
  {
    dataIndex: 'po_diag_desc',
    key: 'po_diag_desc',
    title: FieldLabels.ITEM_DESCRIPTION,
    flex: 2,
    minWidth: 150,
    // Append the HIS item code (po_diag_no) after the description, e.g.
    // "CT SCAN LT ELBOW, 1003". Shown even when no items_master row exists for
    // that code, so a user can tell the admin exactly which code + description
    // to create (which then lets the modality resolve on Check).
    render: (txt, rec) => {
      const desc = rec?.po_diag_desc || '';
      const code = rec?.po_diag_no;
      const full = code ? `${desc}, ${code}` : desc;
      return (
        <Tooltip title={full}>
          <span style={ellipsisCell}>
            {desc}
            {code ? <span style={{ color: 'var(--pacs-faint-fg, #94A3B8)' }}>, {code}</span> : null}
          </span>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'modality',
    key: 'modality',
    title: FieldLabels.MODALITY_SHORT,
    width: 74,
    // Resolved modality pill; empty (no items_master match) renders N/A plus
    // an inline "Check" button that re-resolves after the item code is added.
    render: (text, rec) => <OrderModalityCell rec={rec} allowRecheck={allowModalityRecheck} />,
  },
  {
    dataIndex: 'po_ref_doc',
    key: 'po_ref_doc',
    title: FieldLabels.REFERRING_PHYSICIAN_SHORT,
    width: 138,
    render: (text, rec) => {
      const refDoc = rec?.po_ref_doc;
      return (
        <Tooltip title={refDoc}>
          <div style={ellipsisCell}>{refDoc}</div>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_pat_class',
    key: 'po_pat_class',
    title: FieldLabels.REGISTRATION_TYPE,
    width: 76,
    align: 'center',
    render: (_text, rec) => formatRegType(rec?.po_pat_class),
  },
  {
    dataIndex: 'po_priority',
    key: 'po_priority',
    title: FieldLabels.PRIORITY,
    width: 82,
    render: (_text, rec) => {
      const priority = rec?.po_priority;
      const isUrgent = ['EMERGENCY', 'ASAP'].includes(String(priority || '').toUpperCase());
      return (
        <span style={{ color: isUrgent ? 'var(--pacs-urgent-fg, #991B1B)' : 'var(--pacs-cell-fg, #334155)', fontWeight: isUrgent ? 600 : 400 }}>
          {titleCase(priority)}
        </span>
      );
    },
  },
  {
    dataIndex: 'po_ord_no',
    key: 'po_ord_no',
    title: FieldLabels.ORDER_NUMBER,
    width: 94,
    render: (text, rec) => rec?.po_his_ord_no,
  },
  {
    dataIndex: 'po_acc_no',
    key: 'po_acc_no',
    title: FieldLabels.ACCESSION_NUMBER_SHORT,
    width: 102,
    render: (text, rec) => rec?.po_acc_no,
  },
  // Technician assignment dropdown sits right after Acc No (technicians only).
  ...(isTechnician ? [technicianColumn] : []),
  {
    dataIndex: 'po_req_time',
    key: 'sched_order_dt',
    title: FieldLabels.ORDER_DATETIME,
    width: 140,
    render: (_text, rec) => {
      const appt = formatDateTime(rec?.po_appointment_time);
      const ord = formatDateTime(rec?.po_req_time);
      if (!appt) {
        return <span style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>{ord}</span>;
      }
      return (
        <div style={{ lineHeight: '13px', fontSize: '11px' }}>
          <div style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{appt}</div>
          {ord && <div style={{ color: 'var(--pacs-muted-fg, #64748b)', whiteSpace: 'nowrap' }}>({ord})</div>}
        </div>
      );
    },
  },
  {
    dataIndex: 'po_status',
    key: 'po_status',
    title: statusColumnTitle,
    // Fits the widest label of the shared 13px badge ("CANCELLED"/"NOT READY")
    // plus pill chrome and cell padding; 94 was sized for the old 10px pill.
    width: 118,
    render: (text, record) => {
      const pacs_status = record?.order_workflow?.ow_pacs_status;
      return (
        <Tag
          className="custom-status-tag"
          style={getStatusStyle(pacs_status)}
        >
          {getOrdersStatusLabel(pacs_status)}
        </Tag>
      );
    },
  },
  {
    dataIndex: 'reportedBy',
    key: 'reported_by',
    title: FieldLabels.REPORTED_BY,
    width: 138,
    render: (text, rec) => {
      const name = rec?.reportedBy ?? rec?.order_workflow?.ow_reported_by ?? rec?.pr_reported_by ?? '';
      return (
        <Tooltip title={name}>
          <div style={ellipsisCell}>{name}</div>
        </Tooltip>
      );
    },
  },
  // Technician actions column (documentation uploads).
  ...(isTechnician ? [technicianActionsColumn] : []),
  ...(canDelete
    ? [
        {
          dataIndex: 'actions',
          key: 'actions',
          title: FieldLabels.ACTIONS,
          fixed: 'right',
          width: 56,
          render: (_, rec) => (
            <Tooltip title="Delete order">
              <Button
                type="text"
                size="small"
                icon={<DeleteIcon size={18} />}
                onClick={() => onDelete && onDelete(rec)}
              />
            </Tooltip>
          ),
        },
      ]
    : []),
  ].filter((col: any) => !col.hidden);
};


/**
 * Quick-filter chip row. Renders the user's enabled master shortcuts + their own
 * shortcuts (the unified list produced by mergeShortcuts) as uniform neutral
 * pills. `savedFilters` / `handleFilterSelection` are accepted for backward
 * compatibility but no longer rendered (the Saved-views dropdown was retired in
 * favour of the Shortcut Settings dialog).
 */
export const SavedSearches = ({ quickFilters = [], handleQuickFilters, currentFilters = {}, extraChips = null }) => {
  const [flashingKey, setFlashingKey] = useState(null);

  const pillBase: React.CSSProperties = {
    height: '24px',
    padding: '0 10px',
    borderRadius: '6px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '24px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    userSelect: 'none',
  };

  const getPillStyle = (active: boolean, flashing: boolean): React.CSSProperties => {
    if (active || flashing) {
      return {
        ...pillBase,
        background: '#2F6FED',
        border: '1px solid #2F6FED',
        color: '#FFFFFF',
      };
    }
    return {
      ...pillBase,
      background: 'var(--pacs-chip-bg, #F1F5F9)',
      border: '1px solid var(--pacs-chip-border, #E2E8F0)',
      color: 'var(--pacs-chip-fg, #334155)',
    };
  };

  if ((!quickFilters || quickFilters.length === 0) && !extraChips) {
    return null;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      {quickFilters && quickFilters.length > 0 && (
        <span style={{ fontWeight: 600, fontSize: '12px', color: 'var(--pacs-muted-fg, #64748b)', marginRight: '2px' }}>
          Shortcuts:
        </span>
      )}

      {quickFilters.map((sc) => {
        const active = isShortcutActive(sc, currentFilters);
        const flashing = flashingKey === sc.key;
        const handleClick = () => {
          handleQuickFilters(sc);
          setFlashingKey(sc.key);
          setTimeout(() => setFlashingKey(null), 700);
        };
        return (
          <Tooltip key={sc.key} title={summarizeShortcut(sc.filterRaw)}>
            <button
              onClick={handleClick}
              style={getPillStyle(active, flashing)}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              {sc.name}
            </button>
          </Tooltip>
        );
      })}
      {extraChips}
    </div>
  );
};

// Old QuickFilters component - COMMENTED OUT - Now using SavedSearches component for quick filters
// This component was at wrong placement, replaced with dynamic quick filters in SavedSearches component
// export const QuickFilters = ({ quickFilters, handleQuickFilters }) => {
//   return (
//     <div className="mb-2">
//       {quickFilters.map((filter, index) => (
//         <span className="quick-filters ml-3" key={filter.qf_name} onClick={() => handleQuickFilters(filter)}>
//           {filter.qf_name}
//         </span>
//       ))}
//     </div>
//   )
// }
